import React, { useCallback, useEffect, useRef } from 'react';
import { FastenStitchElement, type FastenStitchElementHandle } from '@fastenhealth/fasten-stitch-element-react';
import { TefcaConnectProps, TefcaWidgetEvent } from './types';

export type { TefcaConnectProps, TefcaWidgetEvent, TefcaConnectionData, TefcaWidgetEventType } from './types';

const EVENT_TYPES =
    'widget.complete,widget.close,widget.config_error,patient.connection_pending,patient.connection_success';

/** The only origin allowed to drive events; identity proofing navigates the same iframe to other origins. */
const FASTEN_EMBED_ORIGIN = 'https://embed.connect.fastenhealth.com';

/**
 * Fasten's Stitch widget in TEFCA mode, web only. The SDK renders a native dialog with an iframe to Fasten's
 * embed page; the mode is decided by the public id (a sandbox key here), so no environment is set. The dialog
 * opens as soon as the session is known and the SDK's own button stays available to reopen it.
 */
const TefcaConnect: React.FC<TefcaConnectProps> = ({ publicId, externalId, onEvent, reopenLabel }) => {
    const widgetRef = useRef<FastenStitchElementHandle>(null);

    useEffect(() => {
        widgetRef.current?.show();
    }, [publicId, externalId]);

    const handleEventBus = useCallback(
        (event: MessageEvent) => {
            if (event.origin !== FASTEN_EMBED_ORIGIN) return;
            let parsed: TefcaWidgetEvent | undefined;
            try {
                parsed = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            } catch {
                return;
            }
            if (parsed?.event_type) onEvent(parsed);
        },
        [onEvent],
    );

    return (
        <FastenStitchElement
            ref={widgetRef}
            publicId={publicId}
            externalId={externalId}
            tefcaMode
            eventTypes={EVENT_TYPES}
            onEventBus={handleEventBus}
            staticBackdrop
            buttonLabel={reopenLabel}
        />
    );
};

export default React.memo(TefcaConnect);
